// TODO(maps-cost): PERMANENT FIX — persist coordinates on the Job entity.
// Root cause of high Geocoding usage: Job stores only Address (string), so every
// map render must resolve the address to a point. This cache is a stopgap
// (per-browser, clears with localStorage). To eliminate geocoding entirely:
//   0. (Places) Replace legacy Autocomplete with PlaceAutocompleteElement +
//      explicit session tokens to bill per-session instead of per-keystroke.
//   1. Add Latitude/Longitude (double?) to Job.cs + EF migration (DispatchData).
//   2. Expose lat/lng in Job DTOs (JobDTO / JobListItemDTO) and API responses.
//   3. On job create/update, capture place.geometry.location from the autocomplete
//      PlaceResult (already free in the Places session) and save it — see the
//      place_changed handler in useGoogleMapsAutocomplete.ts. Fall back to a
//      one-time server-side geocode when an address is set without autocomplete.
//   4. MultiMarkerMap already prefers loc.lat/loc.lng over address — feed the
//      stored coords through MapPage's mapLocations so jobs never geocode.
// Result: geocoding drops to ~0; coords computed once at write time, not per view.

export interface LatLng {
  lat: number;
  lng: number;
}

// A failed entry records an address Google couldn't resolve (ZERO_RESULTS), so a bad
// address is billed once per FAILED_TTL_MS instead of on every map render.
type CacheEntry = (LatLng & { expiresAt: number }) | { failed: true; expiresAt: number };

const STORAGE_KEY = 'dispatch.geocodeCache';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days — addresses effectively never move
const FAILED_TTL_MS = 7 * 24 * 60 * 60 * 1000; // shorter: someone may fix the address

const normalize = (address: string) => address.trim().toLowerCase();

// In-memory mirror, hydrated once from localStorage.
const cache = new Map<string, CacheEntry>();
let hydrated = false;

function hydrate() {
  if (hydrated) return;
  hydrated = true;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, CacheEntry>;
    const now = Date.now();
    for (const [key, entry] of Object.entries(parsed)) {
      if (entry && entry.expiresAt > now) cache.set(key, entry);
    }
  } catch {
    // Corrupt/unavailable storage — start empty.
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(cache)));
  } catch {
    // Storage full/unavailable — the in-memory cache still works this session.
  }
}

/** `undefined` = not cached (geocode it); `null` = known to be unresolvable (don't). */
export function getCachedGeocode(address: string): LatLng | null | undefined {
  if (!address?.trim()) return undefined;
  hydrate();
  const key = normalize(address);
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (entry.expiresAt <= Date.now()) {
    cache.delete(key);
    persist();
    return undefined;
  }
  if ('failed' in entry) return null;
  return { lat: entry.lat, lng: entry.lng };
}

export function setCachedGeocodeFailure(address: string): void {
  if (!address?.trim()) return;
  hydrate();
  cache.set(normalize(address), { failed: true, expiresAt: Date.now() + FAILED_TTL_MS });
  persist();
}

export function setCachedGeocode(address: string, latLng: LatLng): void {
  if (!address?.trim()) return;
  hydrate();
  cache.set(normalize(address), { ...latLng, expiresAt: Date.now() + TTL_MS });
  persist();
}
